/**
 * Property Service
 *
 * Handles property-related API calls including address search,
 * property validation, and NSW Planning API integrations.
 */

import { apiService, resolveTenantId } from './api.service.js';
import { getEndpoint } from '../config/endpoints.js';
import { userStore } from '../stores/user.store.js';
import { mapGeoPropertyReport } from '../utils/geo-property-report.js';

/**
 * Build the request body for /v4/geo/property/report.
 *
 * Defaults to document applicability because all current callers hit the same
 * canonical report endpoint and downstream chat context expects the report
 * response to include applicable documents.
 *
 * Returns null when tenant cannot be resolved — callers must treat this as a
 * fail-closed signal. We never substitute a synthetic tenant id, because
 * doing so would silently aggregate property data from unresolvable sessions
 * under a single shared tenant identifier on the upstream side.
 */
function buildGeoReportBody(propId, { withReportLinks = false, withDocuments = true, enrichmentMode = 'fast' } = {}) {
    const tenantId = resolveTenantId();
    if (!tenantId) return null;

    return {
        tenant_id: tenantId,
        jurisdiction: 'nsw',
        provider_hint: 'nsw',
        provider_enrichment_mode: enrichmentMode,
        include_report_links: withReportLinks,
        include_documents: withDocuments,
        include_development_applications: false,
        property_id: String(propId)
    };
}

/**
 * Build the request body for /v4/geo/location/lookup.
 *
 * The canonical lookup endpoint uses a discriminated `lookup_type` value plus
 * a type-specific identifier field. Discriminator value → identifier field:
 *   - 'address'      → query
 *   - 'lot_number'   → lot_number
 *   - 'cad_id'       → cadastre_id   (matches upstream/packages PR #244)
 *   - 'property_id'  → property_id
 *
 * Returns null when tenant cannot be resolved — callers must treat this as a
 * fail-closed signal. We never substitute a synthetic tenant id, because
 * doing so would silently aggregate unresolvable sessions under one shared
 * tenant in upstream logs and rate-limit buckets.
 */
function buildLookupBody(type, value, { limit } = {}) {
    const tenantId = resolveTenantId();
    if (!tenantId) return null;

    const body = {
        tenant_id: tenantId,
        jurisdiction: 'nsw',
        provider_hint: 'nsw',
        lookup_type: type
    };
    if (limit != null) body.limit = limit;

    switch (type) {
        case 'address':
            body.query = value;
            break;
        case 'lot_number':
            body.lot_number = value;
            break;
        case 'cad_id':
            body.cadastre_id = value;
            break;
        case 'property_id':
            body.property_id = value;
            break;
    }
    return body;
}

/**
 * Parse a /v4/geo/location/lookup response into the legacy
 * { address, propId, gurasId, cadId, lot } shape.
 *
 * Accepts both the canonical `candidates[]` envelope and the legacy
 * `results[]` envelope so non-NSW providers that may still return the
 * old shape do not break the widget.
 */
function parseLookupCandidates(response) {
    const list = response?.candidates ?? response?.results ?? [];
    return list.map((c) => {
        // Legacy results[] can carry `raw` as either an object
        // ({ propId, GURASID, ... }) OR a scalar (the old searchLot step 2
        // returned `{ raw: 456 }` where 456 was the prop_id directly).
        const rawIsObject = c.raw !== null && typeof c.raw === 'object';
        const rawScalar = !rawIsObject ? c.raw : null;
        const rawObject = rawIsObject ? c.raw : {};

        return {
            address: c.address ?? '',
            propId: String(
                c.property_id ?? rawObject.propId ?? rawObject.prop_id ?? rawObject.raw ?? rawScalar ?? c.prop_id ?? ''
            ),
            gurasId: String(c.guras_id ?? rawObject.GURASID ?? rawObject.guras_id ?? ''),
            cadId: c.cadastre_id ?? rawObject.cad_id ?? rawObject.cadId ?? c.cad_id ?? null,
            lot: c.parcel_ref ?? c.lot ?? null
        };
    });
}

function normalizeDocumentIds(ids) {
    if (!Array.isArray(ids)) return [];

    const seen = new Set();
    const normalized = [];
    for (const id of ids) {
        if (typeof id !== 'string') continue;
        const trimmed = id.trim();
        if (!trimmed || seen.has(trimmed)) continue;
        seen.add(trimmed);
        normalized.push(trimmed);
    }
    return normalized;
}

function mapReportPropertyPrecinct(report, propId) {
    if (!report || typeof report !== 'object') return null;

    const precincts = report.planning_controls?.precincts;
    const firstPrecinct = precincts?.[0];
    // Keep spatial membership separate from document applicability IDs.
    const precinctValue =
        firstPrecinct?.code ??
        (typeof firstPrecinct?.value === 'string' ? firstPrecinct.value : undefined) ??
        firstPrecinct?.label ??
        '';
    const precinctFields = Array.isArray(precincts) ? { precinct_value: precinctValue } : {};
    const applicability = report.document_applicability || {};
    const diagnostics = applicability.diagnostics || undefined;
    const allDocuments = Array.isArray(applicability.documents) ? applicability.documents : [];
    const primaryDocuments = Array.isArray(applicability.primary_documents) ? applicability.primary_documents : [];
    const filterDocumentIds = normalizeDocumentIds(applicability.filter?.document_ids);
    const primaryDocumentIds = normalizeDocumentIds(primaryDocuments.map((document) => document?.document_id));
    const broadDocumentIds = normalizeDocumentIds([
        ...(Array.isArray(applicability.document_ids) ? applicability.document_ids : []),
        ...allDocuments.map((document) => document?.document_id)
    ]);
    const documentIds = filterDocumentIds.length
        ? filterDocumentIds
        : primaryDocumentIds.length
          ? primaryDocumentIds
          : broadDocumentIds;

    if (!documentIds.length) {
        return diagnostics || precinctValue
            ? {
                  ...precinctFields,
                  prop_id: String(
                      report.property_id ??
                          report.parcel?.property_id ??
                          report.parcel?.match?.property_id ??
                          propId ??
                          ''
                  ),
                  filter: { document_ids: [] },
                  primary_documents: [],
                  diagnostics
              }
            : null;
    }

    const primaryDocumentsById = new Map();
    for (const document of [...primaryDocuments, ...allDocuments]) {
        if (typeof document?.document_id !== 'string') continue;
        const documentId = document.document_id.trim();
        if (!documentId || !documentIds.includes(documentId) || primaryDocumentsById.has(documentId)) continue;
        primaryDocumentsById.set(documentId, {
            document_id: documentId,
            title: document.title || undefined
        });
    }

    return {
        ...precinctFields,
        prop_id: String(
            report.property_id ?? report.parcel?.property_id ?? report.parcel?.match?.property_id ?? propId ?? ''
        ),
        filter: { document_ids: documentIds },
        primary_documents: [...primaryDocumentsById.values()],
        diagnostics
    };
}

function activePrecinctFromPropertyPrecinct(propertyPrecinct) {
    return (
        propertyPrecinct?.precinct_value ??
        (propertyPrecinct?.primary_documents?.[0]?.document_id || propertyPrecinct?.filter?.document_ids?.[0] || '')
    );
}

/**
 * PropertyService class - handles property-related operations
 */
class PropertyService {
    /**
     * Search for addresses using v3 backend proxy
     * @param {string} query - Search query
     * @param {number} maxResults - Maximum results to return
     * @returns {Promise<Array>} - Array of address suggestions with { address, propId }
     */
    async searchAddress(query, maxResults = 3, options = {}) {
        if (!query || query.length < 1) {
            return [];
        }

        try {
            const apiUrl = getEndpoint('addressLookup');
            const body = buildLookupBody('address', query, { limit: maxResults });
            if (!body) return []; // fail closed when tenant unresolved
            const response = options.signal
                ? await apiService.post(apiUrl, body, { signal: options.signal })
                : await apiService.post(apiUrl, body);
            return parseLookupCandidates(response);
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            console.error('Address search error:', error);
            return [];
        }
    }

    /**
     * Resolve a lot number to a full address via chained address-lookup calls.
     *
     * Chain: lot_number → cad_id → prop_id (from raw) → address → guras_id
     *
     * @param {string} lotNumber - Lot number (e.g., "10/-/DP1234")
     * @returns {Promise<Object|null>} - Resolved { address, propId, gurasId } or null
     */
    async searchLot(lotNumber, options = {}) {
        if (!lotNumber || lotNumber.length < 1) {
            return null;
        }

        try {
            const apiUrl = getEndpoint('addressLookup');

            // Each step's body is built fresh so a mid-chain tenant change
            // fails closed at that step rather than silently continuing.
            const step1Body = buildLookupBody('lot_number', lotNumber, { limit: 10 });
            if (!step1Body) return null;
            const lotResponse = options.signal
                ? await apiService.post(apiUrl, step1Body, { signal: options.signal })
                : await apiService.post(apiUrl, step1Body);
            const [lotCandidate] = parseLookupCandidates(lotResponse);
            const cadId = lotCandidate?.cadId;
            if (!cadId) return null;

            const step2Body = buildLookupBody('cad_id', String(cadId));
            if (!step2Body) return null;
            const cadResponse = options.signal
                ? await apiService.post(apiUrl, step2Body, { signal: options.signal })
                : await apiService.post(apiUrl, step2Body);
            const [cadCandidate] = parseLookupCandidates(cadResponse);
            const propId = cadCandidate?.propId;
            if (!propId) return null;

            const step3Body = buildLookupBody('property_id', String(propId));
            if (!step3Body) return null;
            const propResponse = options.signal
                ? await apiService.post(apiUrl, step3Body, { signal: options.signal })
                : await apiService.post(apiUrl, step3Body);
            const [propCandidate] = parseLookupCandidates(propResponse);
            const address = propCandidate?.address;
            if (!address) return null;

            const step4Body = buildLookupBody('address', address, { limit: 1 });
            if (!step4Body) return null;
            const addrResponse = options.signal
                ? await apiService.post(apiUrl, step4Body, { signal: options.signal })
                : await apiService.post(apiUrl, step4Body);
            const [addrCandidate] = parseLookupCandidates(addrResponse);

            return {
                address,
                propId: String(propId),
                gurasId: addrCandidate?.gurasId || propCandidate?.gurasId || ''
            };
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            console.error('Lot search error:', error);
            return null;
        }
    }

    /**
     * Validate property and get details from backend API
     *
     * v3 Payload: { address, property_id }
     * v3 Response: { council_id, council_name, zone, zone_description, lot_size,
     *               bushfire_restrictions, flood_restrictions, flood_lookup_successful,
     *               heritage_restrictions, property_report_url }
     *
     * @param {string} address - Property address
     * @param {string} propId - Property ID
     * @returns {Promise<Object|null>} - Property details
     */
    async validateProperty(address, propId, gurasId = '', options = {}) {
        try {
            const apiUrl = getEndpoint('addressCheck');

            // Canonical /v4/geo/property/report body. Request both report links
            // (so property_report_url is populated) and documents (so future
            // precinct flow works once backend wires document_applicability).
            const requestBody = buildGeoReportBody(propId, {
                withReportLinks: true,
                withDocuments: true,
                enrichmentMode: 'full'
            });
            if (!requestBody) return null; // fail closed when tenant unresolved

            const response = options.signal
                ? await apiService.post(apiUrl, requestBody, { signal: options.signal })
                : await apiService.post(apiUrl, requestBody);
            const processedResponse = this._processAddressCheckResponse(response, address, propId);
            const propertyPrecinct = mapReportPropertyPrecinct(response, propId);
            const activePrecinct = activePrecinctFromPropertyPrecinct(propertyPrecinct);

            if (processedResponse?.council_name?.trim()) {
                // Store property data. gurasId comes from the prior
                // /geo/location/lookup candidate the caller just selected —
                // the canonical /geo/property/report endpoint does not echo
                // it, and reading from userStore would persist the previous
                // property's value across a re-selection.
                userStore.setProperty({
                    address: processedResponse.address || address,
                    propId: processedResponse.propId || processedResponse.prop_id || propId,
                    gurasId: String(gurasId || ''),
                    zone: processedResponse.zone,
                    zoneDescription: processedResponse.zone_description,
                    lotSize: processedResponse.lot_size,
                    // INVARIANT: stored from this awaited report, before the first chat
                    // question can be sent. '' clears the previous property's width.
                    lotWidth: processedResponse.lot_width != null ? String(processedResponse.lot_width) : '',
                    floorSpaceRatio: processedResponse.floor_space_ratio,
                    minLotSize: processedResponse.minimum_lot_size,
                    maxBuildingHeight: processedResponse.maximum_building_height,
                    mineSubsidenceAffects: processedResponse.mine_subsidence_affects,
                    bushFireZone: processedResponse.bushfire_restrictions ?? false,
                    floodZone: processedResponse.flood_restrictions ?? false,
                    heritageZone: processedResponse.heritage_restrictions ?? false,
                    heritageItemId: processedResponse.heritage_item_id || '',
                    propertyReportUrl: processedResponse.property_report_url || '',
                    isBattleAxeLot: processedResponse.is_battle_axe_lot ?? false,
                    isCornerLot: processedResponse.is_corner_lot ?? false,
                    activePrecinct,
                    propertyPrecinct
                });

                // NOTE: Do NOT call userStore.setCouncil() here.
                // The address lookup council is only for comparison, not for overriding
                // the site council (configured via URL parameter).
            }

            return processedResponse;
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            console.error('Property validation error:', error);
            return null;
        }
    }

    /**
     * Map a canonical /v4/geo/property/report response into the legacy
     * address-check shape used by validateProperty's userStore writes.
     *
     * @param {Object} response - Canonical GeoService property report
     * @param {string} address  - Original address (preserved for downstream UI)
     * @param {string} propId   - Original property ID
     * @returns {Object|null}
     */
    _processAddressCheckResponse(response, address, propId) {
        const mapped = mapGeoPropertyReport(response);
        if (!mapped) return null;

        const propertyPrecinct = mapReportPropertyPrecinct(response, propId);

        return {
            address: address,
            prop_id: propId,
            propId: propId,
            // guras_id intentionally omitted: canonical /geo/property/report does
            // not return it. The caller preserves the value selected from the
            // earlier /geo/location/lookup candidate.
            council_name: mapped.council_name,
            council_url: mapped.council_url,
            council_id: mapped.council_id,
            zone: mapped.zone,
            zone_description: mapped.zone_description,
            lot_size: mapped.lot_size,
            floor_space_ratio: mapped.floor_space_ratio,
            minimum_lot_size: mapped.minimum_lot_size,
            maximum_building_height: mapped.maximum_building_height,
            bushfire_restrictions: mapped.bushfire_restrictions,
            flood_restrictions: mapped.flood_lookup_successful ? mapped.flood_restrictions : null,
            flood_lookup_successful: mapped.flood_lookup_successful,
            heritage_restrictions: mapped.heritage_restrictions,
            mine_subsidence_affects: mapped.mine_subsidence_affects,
            heritage_item_id: mapped.heritage_item_id,
            property_report_url: mapped.property_report_url,
            enrichment_incomplete: mapped.enrichment_incomplete,
            report_link_unavailable: mapped.report_link_unavailable,
            lot_width: mapped.lot_width,
            is_battle_axe_lot: mapped.is_battle_axe_lot,
            is_corner_lot: mapped.is_corner_lot,
            active_precinct: activePrecinctFromPropertyPrecinct(propertyPrecinct),
            property_precinct: propertyPrecinct,
            propertyPrecinct
        };
    }

    /**
     * Get property information including minimum lot size and maximum building height
     *
     * v3 Payload: { address }
     * v3 Response: { maximum_building_height, minimum_lot_size }
     *
     * @param {string} propId - Property ID (unused, kept for API compatibility)
     * @param {string} address - Property address
     * @returns {Promise<Object|null>} - Property info with minimum_lot_size and maximum_building_height
     */
    async getPropertyInfo(propId, address) {
        try {
            if (!propId && !address) return null;

            const apiUrl = getEndpoint('propertyInfo');

            // Canonical /v4/geo/property/report is keyed on property_id.
            // Legacy callers may pass only `address` (the previous endpoint
            // accepted address-only payloads); in that case resolve propId
            // via location/lookup first so the report request is well-formed.
            let effectivePropId = propId;
            if (!effectivePropId) {
                const [match] = await this.searchAddress(address, 1);
                effectivePropId = match?.propId;
                if (!effectivePropId) return null;
            }

            const requestBody = buildGeoReportBody(effectivePropId);
            if (!requestBody) return null;

            const response = await apiService.post(apiUrl, requestBody);
            const processedResponse = this._processPropertyInfoResponse(response);

            // Extract property data from response
            const minLotSize = processedResponse?.minLotSize || '';
            const maxBuildingHeight = processedResponse?.maxBuildingHeight || '';

            // Update user store with these values
            if (minLotSize || maxBuildingHeight) {
                userStore.setProperty({
                    minLotSize,
                    maxBuildingHeight
                });
            }

            return processedResponse;
        } catch (error) {
            console.error('Property info error:', error);
            return null;
        }
    }

    /**
     * Process v3 property info response into standard format
     * @param {Object} response - Raw API response
     * @returns {Object} - Processed response
     */
    _processPropertyInfoResponse(response) {
        const mapped = mapGeoPropertyReport(response);
        if (!mapped) return null;

        return {
            maxBuildingHeight: mapped.maximum_building_height,
            minLotSize: mapped.minimum_lot_size
        };
    }
}

// Export singleton instance
export const propertyService = new PropertyService();
